import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CopyFromProjectSelector } from "@/components/project-detail/CopyFromProjectSelector";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useCreateProjectFrom, useProjects } from "@/hooks/useProjects";

const createProjectSchema = z.object({
  name: z.string().min(1, "Name is required").max(255, "Name is too long"),
  sourceProjectId: z.string().uuid().nullable().optional(),
});

type CreateProjectFormValues = z.infer<typeof createProjectSchema>;

interface CreateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (projectId: string) => void;
}

export function CreateProjectDialog({
  open,
  onOpenChange,
  onSuccess,
}: CreateProjectDialogProps) {
  const createProject = useCreateProjectFrom();
  const { data: projects = [] } = useProjects();

  const form = useForm<CreateProjectFormValues>({
    resolver: zodResolver(createProjectSchema),
    defaultValues: {
      name: "",
      sourceProjectId: null,
    },
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      form.reset({
        name: "",
        sourceProjectId: null,
      });
    }
  }, [open, form]);

  const handleSourceProjectChange = (projectId: string | null) => {
    form.setValue("sourceProjectId", projectId, { shouldDirty: true });
    const sourceProject = projects.find((project) => project.id === projectId);
    if (sourceProject) {
      form.setValue("name", sourceProject.name, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const handleSubmit = (values: CreateProjectFormValues) => {
    createProject.mutate(
      {
        name: values.name,
        sourceProjectId: values.sourceProjectId ?? null,
      },
      {
        onSuccess: (data) => {
          onOpenChange(false);
          form.reset();
          onSuccess?.(data.id);
        },
      },
    );
  };

  const handleClose = () => {
    onOpenChange(false);
    form.reset();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Project</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="sourceProjectId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Copy from</FormLabel>
                  <FormControl>
                    <CopyFromProjectSelector
                      value={field.value ?? null}
                      onChange={handleSourceProjectChange}
                      placeholder="Select source project"
                      testId="create-project-copy-from"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Project Name *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter project name"
                      {...field}
                      autoFocus
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createProject.isPending || createProject.isDisabled}
                title={
                  createProject.isDisabled ? "Offline, read-only" : undefined
                }
              >
                {createProject.isPending ? "Creating..." : "Create Project"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
